/**
 * Выгрузка таблицы в НАСТОЯЩИЙ .xlsx (OOXML), собранный вручную, без внешних библиотек.
 *
 * Почему ушли от CSV/TSV (было: UTF-16LE + табы, имя .csv):
 *  1. Google Таблицы (и мобильные просмотрщики) не понимают UTF-16LE — заказчик открыл
 *     выгрузку на телефоне и увидел кракозябры. У xlsx понятия «кодировки файла» нет вообще:
 *     внутри всегда UTF-8 XML, и открывается одинаково везде.
 *  2. Excel сам домысливал типы в текстовом файле: телефон «77017222646» превращался
 *     в «7,7E+10», а текст «09.03» — в дату «09.мар». В xlsx тип ячейки задан явно,
 *     поэтому всё, что не настоящее число, остаётся текстом как есть.
 *
 * Зависимостей для zip в проекте нет и добавлять их нельзя, поэтому ZIP-контейнер
 * пишется руками. Записи кладём БЕЗ сжатия (метод 0, stored) — это валидный ZIP,
 * а deflate потребовал бы своей реализации ради экономии размера, которая на наших
 * объёмах (тысячи строк) роли не играет.
 */

/* ─────────────────────────── XML ─────────────────────────── */

/**
 * Экранирование + вырезание управляющих символов.
 * Управляющие (< 0x20, кроме \t \n \r) в XML 1.0 запрещены вообще — если такой символ
 * приедет из Битрикса в комментарии, Excel объявит готовый файл «повреждённым».
 */
function xml(value: string): string {
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

/** A, B, … Z, AA, AB — у выгрузок бывает под 30 колонок, одной буквой не обойтись. */
function colName(index: number): string {
  let n = index + 1;
  let out = '';
  while (n > 0) {
    const rest = (n - 1) % 26;
    out = String.fromCharCode(65 + rest) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Прочерк вместо пустоты — требование заказчика: пустая ячейка выглядит как потеря данных. */
const EMPTY = '—';

/** Текст ячейки для расчёта ширины колонки (не для XML). */
function displayText(v: unknown): string {
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : EMPTY;
  if (v === null || v === undefined) return EMPTY;
  const s = String(v);
  return s.trim() === '' ? EMPTY : s;
}

/**
 * Ячейка. Число — только настоящий number; строку «77017222646» НЕ парсим,
 * иначе вернём ту же болезнь с телефонами, ради которой всё и затевалось.
 * Строки пишем инлайном (t="inlineStr"), чтобы не тащить sharedStrings.xml.
 */
function cellXml(ref: string, v: unknown, styleIndex: number): string {
  const s = styleIndex ? ` s="${styleIndex}"` : '';
  if (typeof v === 'number' && Number.isFinite(v)) {
    return `<c r="${ref}"${s}><v>${v}</v></c>`;
  }
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${xml(displayText(v))}</t></is></c>`;
}

/** Excel запрещает в имени листа [ ] : * ? / \ и больше 31 символа. */
function sheetTitle(name: string): string {
  const clean = (name || '').replace(/[[\]:*?\/\\]/g, ' ').trim().slice(0, 31).trim();
  return clean || 'Лист1';
}

/* ─────────────────────────── ZIP ─────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry {
  name: string;
  nameBytes: Uint8Array;
  data: Uint8Array;
  crc: number;
}

/** Собирает ZIP из stored-записей: локальные заголовки + центральный каталог + EOCD. */
function zip(files: { name: string; data: Uint8Array }[]): Uint8Array {
  const enc = new TextEncoder();
  const entries: ZipEntry[] = files.map((f) => ({
    name: f.name,
    nameBytes: enc.encode(f.name),
    data: f.data,
    crc: crc32(f.data),
  }));

  const localSize = entries.reduce((s, e) => s + 30 + e.nameBytes.length + e.data.length, 0);
  const centralSize = entries.reduce((s, e) => s + 46 + e.nameBytes.length, 0);
  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  let pos = 0;

  const u16 = (v: number) => { view.setUint16(pos, v, true); pos += 2; };
  const u32 = (v: number) => { view.setUint32(pos, v >>> 0, true); pos += 4; };
  const raw = (b: Uint8Array) => { out.set(b, pos); pos += b.length; };

  const offsets: number[] = [];
  for (const e of entries) {
    offsets.push(pos);
    u32(0x04034b50);
    u16(20);          // версия
    u16(0x0800);      // флаг: имена в UTF-8
    u16(0);           // метод 0 — без сжатия
    u16(0); u16(0);   // время/дата — фиксированные, детерминированный файл удобнее
    u32(e.crc);
    u32(e.data.length);
    u32(e.data.length);
    u16(e.nameBytes.length);
    u16(0);
    raw(e.nameBytes);
    raw(e.data);
  }

  const centralStart = pos;
  entries.forEach((e, i) => {
    u32(0x02014b50);
    u16(20); u16(20);
    u16(0x0800);
    u16(0);
    u16(0); u16(0);
    u32(e.crc);
    u32(e.data.length);
    u32(e.data.length);
    u16(e.nameBytes.length);
    u16(0); u16(0); u16(0); u16(0);
    u32(0);
    u32(offsets[i]);
    raw(e.nameBytes);
  });

  // Размер каталога фиксируем ДО записи EOCD: иначе `pos` уже уедет вперёд и ридер
  // (тот же Python zipfile) промахнётся мимо начала каталога.
  const centralSizeActual = pos - centralStart;

  u32(0x06054b50);
  u16(0); u16(0);
  u16(entries.length); u16(entries.length);
  u32(centralSizeActual);
  u32(centralStart);
  u16(0);

  return out;
}

/* ─────────────────────── Части книги ─────────────────────── */

const CONTENT_TYPES = `${XML_HEAD}
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS = `${XML_HEAD}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS = `${XML_HEAD}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

/** Два шрифта: обычный и жирный (для шапки). Стиль s="1" = жирный. */
const STYLES = `${XML_HEAD}
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="2">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="2">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

/* ─────────────────────────── API ─────────────────────────── */

/**
 * Чистая генерация книги: ни DOM, ни Blob — можно позвать из Node и проверить тестом.
 */
export function buildXlsx(sheetName: string, headers: string[], rows: unknown[][]): Uint8Array {
  const colCount = Math.max(headers.length, ...rows.map((r) => r.length), 1);

  // Ширины: смотрим и заголовок, и содержимое, но упираемся в потолок —
  // иначе один длинный комментарий растянет колонку на весь экран.
  const widths: number[] = [];
  for (let c = 0; c < colCount; c++) {
    let max = displayText(headers[c] ?? '').length;
    for (const row of rows) {
      const len = displayText(row[c]).length;
      if (len > max) max = len;
    }
    widths.push(Math.min(60, Math.max(8, max + 2)));
  }

  const cols = `<cols>${widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('')}</cols>`;

  const lines: string[] = [];
  lines.push(
    `<row r="1">${Array.from({ length: colCount }, (_, c) =>
      cellXml(`${colName(c)}1`, headers[c] ?? '', 1)).join('')}</row>`,
  );
  rows.forEach((row, r) => {
    const n = r + 2;
    lines.push(
      `<row r="${n}">${Array.from({ length: colCount }, (_, c) =>
        cellXml(`${colName(c)}${n}`, row[c], 0)).join('')}</row>`,
    );
  });

  const lastRef = `${colName(colCount - 1)}${rows.length + 1}`;
  const sheet = `${XML_HEAD}
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="A1:${lastRef}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${cols}
<sheetData>${lines.join('')}</sheetData>
</worksheet>`;

  const workbook = `${XML_HEAD}
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${xml(sheetTitle(sheetName))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  const enc = new TextEncoder();
  return zip([
    { name: '[Content_Types].xml', data: enc.encode(CONTENT_TYPES) },
    { name: '_rels/.rels', data: enc.encode(ROOT_RELS) },
    { name: 'xl/workbook.xml', data: enc.encode(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: enc.encode(WORKBOOK_RELS) },
    { name: 'xl/styles.xml', data: enc.encode(STYLES) },
    { name: 'xl/worksheets/sheet1.xml', data: enc.encode(sheet) },
  ]);
}

/**
 * Скачать таблицу как .xlsx. Сигнатура сохранена: вызывающие экраны по-прежнему
 * передают имя с «.csv», поэтому расширение подменяем здесь, а не в трёх местах.
 */
export function downloadTableExcel(filename: string, headers: string[], rows: unknown[][]): void {
  const base = filename.replace(/\.(csv|tsv|xlsx?)$/i, '');
  const name = `${base}.xlsx`;
  const data = buildXlsx(base, headers, rows);
  // Копируем в свежий ArrayBuffer: Blob не должен зависеть от возможного view-окна.
  const blob = new Blob([data.slice().buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  // Ссылку обязательно кладём в документ: click() по элементу вне DOM часть браузеров
  // игнорирует вместе с атрибутом download — файл падал без имени и расширения .xlsx.
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Освобождаем адрес не сразу: скачивание стартует асинхронно, и мгновенный revoke
  // успевал оборвать его на медленной машине.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
